import { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  deleteTerminologyItem,
  getTerminologyItem,
} from "@/api/terminology";
import type { TerminologyItem } from "@/api/terminology";
import { useToast } from "@/components/toast";

import { SenseSearch } from "../SenseSearch";
import type { SenseTarget } from "../senseSearchTypes";

const TRUSTED = new Set(["human", "regulatory_aligned", "expert_reviewed", ""]);

function isHard(item: TerminologyItem): boolean {
  if (item.item_type !== "glossary_term") return false;
  const ext = (item.external_ids ?? {}) as Record<string, unknown>;
  const ce = ext.constraint_eligible;
  const ms = ext.morph_safe;
  const rts = String(ext.ru_term_source ?? "");
  return ce !== false && ms !== false && TRUSTED.has(rts);
}

export function TermOverviewPanel() {
  const { toast } = useToast();
  const qc = useQueryClient();
  const [picked, setPicked] = useState<SenseTarget | null>(null);

  const detail = useQuery({
    queryKey: ["terminology-item", picked?.item_id],
    queryFn: () => getTerminologyItem(picked!.item_id),
    enabled: !!picked,
  });
  const data = detail.data?.ok ? detail.data.data : null;
  const item = data?.item ?? null;
  const forbidden = data?.replace_aliases ?? [];
  const siblings = data?.siblings ?? [];

  const delMut = useMutation({
    mutationFn: async (aliasItemId: string) => {
      const r = await deleteTerminologyItem(aliasItemId);
      if (!r.ok) throw new Error(r.error);
      return r.data;
    },
    onSuccess: () => {
      toast("Удалено", "success");
      void qc.invalidateQueries({
        queryKey: ["terminology-item", picked?.item_id],
      });
    },
    onError: (e: Error) => toast(e.message, "error"),
  });

  return (
    <div>
      <SenseSearch onPick={setPicked} pickedItemId={picked?.item_id} />
      {picked && detail.isLoading && (
        <div
          className="mt-muted"
          style={{ marginTop: 12, fontSize: "var(--fs-sm)" }}
        >
          Загружаю настройки термина…
        </div>
      )}
      {picked && item && (
        <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 12 }}>
          <div>
            <span className="mt-muted">Перевод: </span>
            <b>{item.target_abbreviation || item.target_text || "—"}</b>{" "}
            <span
              className={
                "mt-badge " +
                (isHard(item) ? "mt-badge--success" : "mt-badge--info") +
                " mt-badge--no-dot"
              }
            >
              {isHard(item) ? "жёстко" : "подсказка"}
            </span>{" "}
            <Link
              className="mt-btn mt-btn--sm mt-btn--ghost"
              to={`/admin/terminology/${encodeURIComponent(picked.item_id)}`}
            >
              открыть карточку →
            </Link>
          </div>

          <div>
            <div className="mt-muted" style={{ marginBottom: 4 }}>
              Запрещённые формы
            </div>
            {forbidden.length === 0 ? (
              <div className="mt-muted" style={{ fontSize: "var(--fs-sm)" }}>
                нет
              </div>
            ) : (
              forbidden.map((f) => (
                <div
                  key={f.item_id ?? f.alias_text}
                  className="rf-example"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    padding: 6,
                    fontSize: "var(--fs-sm)",
                  }}
                >
                  <span style={{ flex: 1 }}>
                    «{f.alias_text}» → «{f.canonical_text}»
                  </span>
                  {f.item_id && (
                    <button
                      type="button"
                      className="mt-btn mt-btn--sm mt-btn--danger mt-btn--ghost"
                      onClick={() => delMut.mutate(f.item_id!)}
                    >
                      убрать
                    </button>
                  )}
                </div>
              ))
            )}
          </div>

          {siblings.length > 0 && (
            <div>
              <div className="mt-muted" style={{ marginBottom: 4 }}>
                Другие смыслы / связанные записи
              </div>
              {siblings.map((s) => (
                <div
                  key={s.item_id}
                  className="rf-example"
                  style={{ padding: 6, fontSize: "var(--fs-sm)" }}
                >
                  <Link to={`/admin/terminology/${encodeURIComponent(s.item_id)}`}>
                    {s.source_text ?? s.source_expansion} →{" "}
                    {s.target_text ?? s.target_expansion}
                    {s.domain ? ` (${s.domain})` : ""}
                  </Link>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
